import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./FAQPage.module.css";
import { normalizeLanguageCode } from "../supportedLanguages";
import faqContent from "../faqContent.json";
import globalFeatureContent from "../globalFeatureContent.json";

type FAQItem = {
  question: string;
  answer: string;
  relatedPath: string;
  relatedLabel: string;
};

type FAQContent = (typeof faqContent)["en"];
type GlobalFeatureContent = (typeof globalFeatureContent)["en"];

const faqContentByLanguage = faqContent as Record<string, FAQContent>;
const globalFeatureContentByLanguage = globalFeatureContent as Record<
  string,
  GlobalFeatureContent
>;

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = normalizeLanguageCode(lang ?? "en");
  const labels = faqContentByLanguage[currentLang] || faqContentByLanguage.en;
  const globalFeatures =
    globalFeatureContentByLanguage[currentLang] || globalFeatureContentByLanguage.en;

  const productAnswer = `${t(
    "home_page.hero.messages.0.subtitle2",
  )} ${t("features_page.hero.subtitle")}`;
  const shortsLongformAnswer = `${t("features_page.templates.subtitle")} ${t(
    "features_page.templates.bullet1",
  )}`;
  const beginnerAnswer = globalFeatures.guidedWorkflowText;
  const effectsAnswer = `${globalFeatures.subtitleEffectsText} ${t(
    "how_to_use.steps.image_motion.body",
  )} ${t("features_page.templates.subtitle")}`;
  const uploadAnswer = `${t("features_page.upload.subtitle")} ${t(
    "features_page.upload.bullet1",
  )}`;
  const marketingAnswer = `${globalFeatures.marketingText} ${globalFeatures.globalText}`;
  const supportAnswer = `${t("how_to_use.hero.subtitle")} ${t(
    "support_page.subtitle",
  )}`;

  const faqItems: FAQItem[] = [
    {
      question: labels.productQuestion,
      answer: productAnswer,
      relatedPath: "/features",
      relatedLabel: t("navigation.features"),
    },
    {
      question: labels.shortsLongformQuestion,
      answer: shortsLongformAnswer,
      relatedPath: "/features",
      relatedLabel: t("navigation.features"),
    },
    {
      question: labels.beginnerQuestion,
      answer: beginnerAnswer,
      relatedPath: "/how-to-use",
      relatedLabel: t("navigation.how_to_use"),
    },
    {
      question: labels.effectsQuestion,
      answer: effectsAnswer,
      relatedPath: "/how-to-use",
      relatedLabel: t("navigation.how_to_use"),
    },
    {
      question: labels.editorQuestion,
      answer: labels.editorAnswer,
      relatedPath: "/features",
      relatedLabel: t("navigation.features"),
    },
    {
      question: labels.marketingQuestion,
      answer: marketingAnswer,
      relatedPath: "/features",
      relatedLabel: t("navigation.features"),
    },
    {
      question: labels.uploadQuestion,
      answer: uploadAnswer,
      relatedPath: "/how-to-use",
      relatedLabel: t("navigation.how_to_use"),
    },
    {
      question: labels.creditsQuestion,
      answer: labels.creditsAnswer,
      relatedPath: "/pricing",
      relatedLabel: t("navigation.pricing"),
    },
    {
      question: labels.commercialQuestion,
      answer: labels.commercialAnswer,
      relatedPath: "/ai-content-policy",
      relatedLabel: t("footer.ai_content_policy", {
        defaultValue: "AI Content Policy",
      }),
    },
    {
      question: labels.supportQuestion,
      answer: supportAnswer,
      relatedPath: "/support",
      relatedLabel: t("support_page.title"),
    },
  ];

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1>
          {t("faq_page.hero.title", {
            defaultValue: "AUTOVID FAQ",
          })}
        </h1>
        <p className={styles.heroSubtitle}>
          {t("faq_page.hero.subtitle", {
            defaultValue: productAnswer,
          })}
        </p>
      </section>

      <section className={styles.faqSection}>
        {faqItems.map((item, index) => (
          <details key={index} className={styles.faqItem}>
            <summary className={styles.question}>{item.question}</summary>
            <p className={styles.answer}>{item.answer}</p>
            <Link
              to={`/${currentLang}${item.relatedPath}`}
              className={styles.answerLink}
            >
              {item.relatedLabel}
              <span aria-hidden="true"> →</span>
            </Link>
          </details>
        ))}
      </section>

      <section className={styles.linkSection}>
        <h2>
          {t("faq_page.links.title", {
            defaultValue: t("support_page.title"),
          })}
        </h2>
        <div className={styles.linkRow}>
          <Link to={`/${currentLang}/support`} className={styles.link}>
            {t("support_page.title")}
          </Link>
          <Link to={`/${currentLang}/how-to-use`} className={styles.link}>
            {t("navigation.how_to_use")}
          </Link>
          <Link to={`/${currentLang}/pricing`} className={styles.link}>
            {t("navigation.pricing")}
          </Link>
          <Link to={`/${currentLang}/refund-policy`} className={styles.link}>
            {t("footer.refund_policy")}
          </Link>
          <Link to={`/${currentLang}/privacy-policy`} className={styles.link}>
            {t("footer.privacy_policy")}
          </Link>
          <Link
            to={`/${currentLang}/terms-of-service`}
            className={styles.link}
          >
            {t("footer.terms_of_service")}
          </Link>
        </div>
        <p className={styles.contact}>
          {t("support_page.subtitle")}
        </p>
      </section>
    </div>
  );
};

export default FAQPage;
